import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardBody, Col, Input, Modal, ModalBody, Row } from "reactstrap";
import { getPhotoUrl } from "../../../config";
import companyImg from "../../../assets/images/companyImg.jpg";
import Pagination from "../../Jobs/JobList2/Pagination";
import { getCompany, putJobOfferStatus, putJobOfferSuspendedStatus, getJobsEnrichedByCompany } from "../../../services/userservice";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useValidUserId, useUserRole } from '../../../hooks';
import { createMountedRef } from '../../../utils/validation';
import { batchStateUpdates } from '../../../utils/apiHelpers';

const PAGE_SIZE_OPTIONS = [5, 10, 20];

const JobListing = () => {
  const [modal, setModal] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [company, setCompany] = useState();
  const [selectedId, setSelectedId] = useState(null);
  const [img, setImg] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const { userId, isValid } = useValidUserId();
  const userRol = useUserRole();

  const filteredJobs = jobs.filter(job => job.status);
  const sortedJobs = [...filteredJobs].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );
  const totalFiltered = sortedJobs.length;
  const totalPages = Math.ceil(totalFiltered / pageSize) || 0;
  const start = totalFiltered === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalFiltered);
  const paginatedJobs = sortedJobs.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  /**
   * Carga los datos de la empresa y los trabajos enriquecidos con applications_count
   */
  useEffect(() => {
    const { isMountedRef, cleanup } = createMountedRef();
    const isMountedCheck = () => isMountedRef.current;

    if (!isValid) return cleanup;

    const loadCompanyData = async () => {
      try {
        const companyData = await getCompany(userId);
        if (!isMountedCheck()) return;

        batchStateUpdates([
          { setter: setCompany, value: companyData.id },
          { setter: setImg, value: companyData.photo }
        ], isMountedCheck);

        const jobsData = await getJobsEnrichedByCompany(companyData.id);
        if (isMountedCheck()) {
          setJobs(jobsData);
        }
      } catch (error) {
        console.error('Error loading company data:', error);
        toast.error('Error al cargar datos de la empresa');
      }
    };

    loadCompanyData();
    return cleanup;
  }, [userId, isValid]);

  const navigate = useNavigate();

  const eliminador = () => {
    putJobOfferStatus(selectedId, false)
      .then(() => {
        toast.success("La oferta de trabajo ha sido eliminada.");
        return getJobsEnrichedByCompany(company);
      })
      .then((data) => {
        setJobs(data);
        setModal(false);
        setSelectedId(null);
      })
      .catch(error => {
        toast.error("Error al eliminar la oferta de trabajo.");
        console.error(error);
      });
  };

  const getApplicationCount = (jobOfferId) => {
    const job = jobs.find(j => j.id === jobOfferId);
    return job?.applications_count ?? 0;
  };

  const editar = (id) => {
    localStorage.setItem('job_id', id);
    navigate('/bookmarkjobpost');
  };

  const openModal = (id) => {
    setModal(!modal);
    setSelectedId(id);
  };

  const suspender = (id) => {
    //Si es true esta suspendida la oferta
    putJobOfferSuspendedStatus(id, true)
      .then(() => {
        toast.success("La oferta de trabajo ha sido suspendida.");
        localStorage.setItem('active_offers', parseInt(localStorage.getItem("active_offers")) - 1)
        getJobsEnrichedByCompany(company)
          .then(data => setJobs(data))
          .catch(error => console.error(error));
      })
      .catch(error => {
        toast.error("Error al suspender la oferta de trabajo.");
        console.error(error);
      });
  };

  const activar = (id) => {
    //Si es false la oferta se activa
    putJobOfferSuspendedStatus(id, false)
      .then(() => {
        toast.success("La oferta de trabajo ha sido activada.");
        localStorage.setItem('active_offers', parseInt(localStorage.getItem("active_offers")) + 1)
        localStorage.setItem('available_credits', parseInt(localStorage.getItem("available_credits")) - 1)
        getJobsEnrichedByCompany(company)
          .then(data => setJobs(data))
          .catch(error => console.error(error));
      })
      .catch(error => {
        toast.error("Error al activar la oferta de trabajo.");
        console.error(error);
      });
  };

  return (
    <React.Fragment>
      <style>
        {`
          .suspended-job {
            border-left: 5px solid var(--bs-danger);
            opacity: 0.6;
          }
          .clickable-text {
            color: var(--bs-primary);
            text-decoration: underline;
            cursor: pointer;
          }
          .candidate-summary-list > div:last-child {
            border-bottom: none !important;
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
          .btn-candidates-postulados {
            background: var(--bs-primary);
            color: #fff;
            border: none;
            border-radius: 0.375rem;
            padding: 0.5rem 0.75rem;
            min-width: 6.25rem;
            text-align: center;
            text-decoration: none;
            display: inline-flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            transition: background-color 0.15s ease-in-out;
          }
          .btn-candidates-postulados:hover {
            filter: brightness(0.95);
            color: #fff;
          }
          .btn-candidates-postulados .candidates-count {
            font-size: 1.75rem;
            font-weight: 700;
            line-height: 1.2;
          }
          .btn-candidates-postulados .candidates-label {
            font-size: 0.65rem;
            font-weight: 600;
            letter-spacing: 0.02em;
            text-transform: uppercase;
            margin-top: 0.125rem;
          }
        `}
      </style>
      <Row>
        <Col xs={12}>
          {userRol === "1" && (
            <div className="list-results-bar">
              <span className="job-grid-results-text">
                Mostrando {totalFiltered === 0 ? 0 : start}-{end} de {totalFiltered} ofertas
              </span>
              <div className="job-grid-page-size-wrap">
                <label htmlFor="pageSizeSelectManage">Ofertas por página:</label>
                <Input
                  id="pageSizeSelectManage"
                  type="select"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="job-grid-page-size-select"
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </Input>
              </div>
            </div>
          )}
          {paginatedJobs.map((jobListingDetails, key) => (
              <Card className={`mt-4 ${jobListingDetails.suspended ? "suspended-job" : ""}`} key={key}>
                <CardBody className="p-4">
                  <Row className="g-3 align-items-center">
                    <Col xs="auto" lg={1} className="flex-shrink-0">
                      <Link to="/companydetails">
                        <img
                          src={getPhotoUrl(img)}
                          alt=""
                          className="img-fluid rounded-3"
                          onError={(e) => { e.target.src = companyImg; }}
                        />
                      </Link>
                    </Col>

                    <Col xs={12} lg={7} className="min-vw-0">
                      <div className="mt-2 mt-md-0">
                        <h5 className="mb-1">
                          <Link
                            to={`/jobdetails/${jobListingDetails.id}`}
                            onClick={() => localStorage.setItem("jobs", jobListingDetails.id)}
                            className="text-dark text-decoration-none"
                          >
                            {jobListingDetails.title}
                          </Link>
                        </h5>
                        <ul className="list-inline text-muted small mb-2">
                          <li className="list-inline-item">{jobListingDetails.modality}</li>
                          <li className="list-inline-item">
                            <i className="mdi mdi-map-marker me-1" aria-hidden />
                            {jobListingDetails.city}
                          </li>
                          <li className="list-inline-item">
                            <i className="uil uil-wallet me-1" aria-hidden />
                            {jobListingDetails.salary}
                          </li>
                        </ul>
                        <div className="d-flex flex-wrap align-items-center gap-2">
                          <span
                            className={
                              jobListingDetails.fullTime === true
                                ? "badge bg-success-subtle text-success"
                                : jobListingDetails.partTime === true
                                ? "badge bg-danger-subtle text-danger"
                                : jobListingDetails.freeLance === true
                                ? "badge bg-primary-subtle text-primary"
                                : jobListingDetails.internship === true
                                ? "badge bg-info-subtle text-info"
                                : "badge bg-secondary-subtle text-secondary"
                            }
                          >
                            {jobListingDetails.timing}
                          </span>
                          <span className="text-muted small">
                            Publicado el: {new Date(jobListingDetails.created_at).toLocaleDateString()}
                          </span>
                          {(jobListingDetails.badges || []).map((badgeInner, badgeKey) => (
                            <span className={`badge ${badgeInner.badgeclassName}`} key={badgeKey}>
                              {badgeInner.badgeName}
                            </span>
                          ))}
                        </div>
                      </div>
                    </Col>

                    <Col xs={12} lg={4} className="d-flex flex-nowrap align-items-center gap-2 pe-0 pe-lg-4 flex-shrink-0">
                      {userRol === "1" && (
                        <Link
                          to={`/listcandidateapply?jobOfferId=${jobListingDetails.id}`}
                          className="btn-candidates-postulados"
                        >
                          <span className="candidates-count">{getApplicationCount(jobListingDetails.id)}</span>
                          <span className="candidates-label">Candidatos postulados</span>
                        </Link>
                      )}
                      <Link
                        to="/bookmarkjobpost"
                        onClick={() => editar(jobListingDetails.id)}
                        className="avatar-sm bg-success-subtle text-success d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                        title="Editar"
                        aria-label="Editar oferta"
                      >
                        <i className="uil uil-edit" />
                      </Link>
                      <Link
                        onClick={(e) => { e.preventDefault(); openModal(jobListingDetails.id); }}
                        to="#"
                        className="avatar-sm bg-danger-subtle text-danger d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                        title="Eliminar"
                        aria-label="Eliminar oferta"
                      >
                        <i className="uil uil-trash-alt" />
                      </Link>
                      {jobListingDetails.suspended ? (
                        <Link
                          onClick={(e) => { e.preventDefault(); activar(jobListingDetails.id); }}
                          to="#"
                          className="avatar-sm bg-success-subtle text-success d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                          title="Activar"
                          aria-label="Activar oferta"
                        >
                          <i className="uil uil-play" />
                        </Link>
                      ) : (
                        <Link
                          onClick={(e) => { e.preventDefault(); suspender(jobListingDetails.id); }}
                          to="#"
                          className="avatar-sm bg-warning-subtle text-warning d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                          title="Suspender"
                          aria-label="Suspender oferta"
                        >
                          <i className="uil uil-pause" />
                        </Link>
                      )}
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            ))}
        </Col>
        <Pagination
          totalItems={totalFiltered}
          pageSize={pageSize}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </Row>

      <div
        className="modal fade"
        id="deleteModal"
        tabIndex="-1"
        aria-labelledby="deleteModal"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <Modal isOpen={modal} toggle={openModal} centered tabIndex="-1">
            <div className="modal-header">
              <h5 className="modal-title" id="staticBackdropLabel">
                Eliminar Empleo
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={openModal}
              ></button>
            </div>
            <ModalBody>
              <div>
                <h6 className="text-danger">
                  <i className="uil uil-exclamation-triangle"></i> Advertencia:
                  ¿Estás seguro de que quieres eliminar la publicación de trabajo?
                </h6>
                <p className="text-muted">
                  {" "}
                  Su publicación de empleo se eliminará de forma permanente y no podrá
                  poder verlos de nuevo, incluso una vez que se comparten con los candidatos.
                </p>
              </div>
            </ModalBody>
            <div className="modal-footer">
              <button
                type="button"
                onClick={openModal}
                className="btn btn-primary btn-sm"
              >
                Cancelar
              </button>
              <button type="button" onClick={eliminador} className="btn btn-danger btn-sm">
                Si, Eliminar
              </button>
            </div>
          </Modal>
        </div>
      </div>

    </React.Fragment>
  );
};

export default JobListing;